/** NOTE:(diwakersurya)
 * @fileoverview
 * Agentic UI event bus (broadcast-only).
 * - Broadcasts CustomEvent to current subscribers only
 * - Optional dedupe within TTL using name+payload or explicit dedupeKey.
 */

import {useEffect, useRef} from 'react';
import type {Product, EventName, EventPayload} from './product-event-registry';

/** Types */
export type Intent = {
  id: string;
  /** Product/channel of the event (e.g., "automation", "crm") */
  product: Product;
  /** Event name (e.g., "workflow_renamed"). Alias: name */
  event: EventName<Product>;
  payload: EventPayload<Product, EventName<Product>>;
  ts: number;
  dedupeKey?: string;
};

/** Strongly typed intent for a known product+event pair */
export type TypedIntent<P extends Product> = {
  id: string;
  product: P;
  event: EventName<P>;
  payload: EventPayload<P, EventName<P>>;
  ts: number;
};
type TypedIntentHandler<P extends Product> = (
  i: TypedIntent<P>,
) => void | Promise<void>;

/** Bus + dedupe */
const bus = new EventTarget();
const seen = new Map<string, number>();
const DEDUPE_TTL = 800;

// Typed callable interface + single implementation to avoid redeclare lint issues
export interface PublishAgenticEvent {
  (
    product: Product,
    event: EventName<Product>,
    payload: EventPayload<Product, EventName<Product>> | null,
    dedupeKey?: string,
  ): void;
}

export const publishAgenticEvent: PublishAgenticEvent = (
  product: Product,
  event: EventName<Product>,
  payload: EventPayload<Product, EventName<Product>> | null,
  dedupeKey?: string,
) => {
  const now = Date.now();
  const compositeKey = `${product ?? 'unknown'}:${event}`;
  const key = dedupeKey ?? `${compositeKey}:${JSON.stringify(payload ?? {})}`;
  const last = seen.get(key) ?? 0;
  if (now - last < DEDUPE_TTL) {
    return;
  }
  seen.set(key, now);
  bus.dispatchEvent(
    new CustomEvent(event, {
      detail: {product, payload, ts: now},
    }),
  );
};

/** Hook: subscribe to an event name (broadcast-only) */
// Typed callable interface + single implementation
// export interface UseRegisterAgenticUIIntent {
//   (
//     product: Product,
//     event: EventName<Product> | TypedIntentHandler,
//     handler: TypedIntentHandler,
//   ): void;
// }

export const useRegisterAgenticUIIntent = <P extends Product>(
  a: P,
  b: EventName<P> | TypedIntentHandler<P>,
  c?: TypedIntentHandler<P>,
) => {
  const hasProduct = typeof b === 'string';
  const productFilter = hasProduct ? (a as Product) : undefined;
  const eventName = hasProduct
    ? (b as EventName<P>)
    : (a as EventName<Product>);
  const handler = (
    hasProduct ? c : (b as TypedIntentHandler<P>)
  ) as TypedIntentHandler<P>;

  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    const listener = (ev: Event) => {
      const ce = ev as CustomEvent;
      const now = Date.now();
      const incomingProduct = ce.detail?.product as P;
      if (productFilter && productFilter !== incomingProduct) {
        return;
      }
      const intent: TypedIntent<P> = {
        id: (crypto as any).randomUUID?.() ?? String(now),
        product: incomingProduct,
        event: eventName,
        payload: ce.detail?.payload as EventPayload<P, EventName<P>>,
        ts: ce.detail?.ts as number,
      };
      void Promise.resolve(ref.current(intent));
    };
    bus.addEventListener(eventName, listener as EventListener);
    return () => {
      bus.removeEventListener(eventName, listener as EventListener);
    };
  }, [productFilter, eventName]);
};
